<template>
	<div
		ref="sliderRef"
		class="relative flex h-8 w-full cursor-pointer touch-none items-center select-none"
		@mousedown.stop="handleMouseDown"
		@touchstart.stop="handleTouchStart"
		@touchend.stop
	>
		<div class="relative h-3 w-full overflow-hidden rounded-full bg-white/10">
			<div
				class="bg-red absolute h-full rounded-full ease-out"
				:style="{ width: `${percentage}%` }"
			></div>
		</div>
		<div
			class="pointer-events-none absolute h-6.5 w-6.5 -translate-x-1/2"
			:style="{ left: `${percentage}%` }"
		>
			<HandleIcon class="h-full w-full" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import HandleIcon from '@/assets/icons/handle.svg'

const props = defineProps<{
	min: number
	max: number
	rangeMin: number
	rangeMax: number
	step: number
}>()

const model = defineModel<number>()

const sliderRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)

const percentage = computed(() => {
	if (!model.value) return 0
	return (
		((model.value - props.rangeMin) / (props.rangeMax - props.rangeMin)) * 100
	)
})

const updateValue = (clientX: number) => {
	if (!sliderRef.value) {
		return
	}

	const rect = sliderRef.value.getBoundingClientRect()
	const offsetX = Math.max(0, Math.min(clientX - rect.left, rect.width))
	const percentage = offsetX / rect.width
	const rawValue = props.min + percentage * (props.max - props.min)

	// Round to nearest step
	const steppedValue = Math.round(rawValue / props.step) * props.step
	const clampedValue = Math.max(props.min, Math.min(props.max, steppedValue))

	model.value = clampedValue
}

const handleMouseDown = (e: MouseEvent) => {
	isDragging.value = true
	updateValue(e.clientX)

	const handleMouseMove = (e: MouseEvent) => {
		if (isDragging.value) {
			updateValue(e.clientX)
		}
	}

	const handleMouseUp = () => {
		isDragging.value = false
		document.removeEventListener('mousemove', handleMouseMove)
		document.removeEventListener('mouseup', handleMouseUp)
	}

	document.addEventListener('mousemove', handleMouseMove)
	document.addEventListener('mouseup', handleMouseUp)
}

const handleTouchStart = (e: TouchEvent) => {
	isDragging.value = true
	const touch = e.touches[0]
	updateValue(touch.clientX)

	const handleTouchMove = (e: TouchEvent) => {
		if (isDragging.value) {
			e.preventDefault() // Prevent scrolling while dragging
			const touch = e.touches[0]
			updateValue(touch.clientX)
		}
	}

	const handleTouchEnd = () => {
		isDragging.value = false
		document.removeEventListener('touchmove', handleTouchMove)
		document.removeEventListener('touchend', handleTouchEnd)
	}

	document.addEventListener('touchmove', handleTouchMove, { passive: false })
	document.addEventListener('touchend', handleTouchEnd)
}
</script>
